import React from "react";
import { useNavigate } from "react-router-dom";
import Footer from "./Footer";
import "./landing.css";

// ─────────────────────────────────────────────────────────────────────────────
// FIRST-DRAFT legal text. Written to match classBoon's ACTUAL data practices
// (student name / ID / grade, attendance, sign-out behavioral logs, lunch
// choices; data stored in Google Firebase; FERPA "school official" + COPPA
// school-consent model). ⚠️ Have a human / attorney review before relying on it
// for a district Data Privacy Agreement. Update SUPPORT_EMAIL + EFFECTIVE_DATE.
// ─────────────────────────────────────────────────────────────────────────────

export const SUPPORT_EMAIL = "support@classboon.com";
const EFFECTIVE_DATE = "June 12, 2026";

function LegalLayout({ title, subtitle, children }) {
  return (
    <div className="about legal">
      <header className="about-hero">
        <div className="landing-inner">
          <h1>{title}</h1>
          {subtitle && <p>{subtitle}</p>}
        </div>
      </header>

      <div className="landing-inner">
        <div className="legal-content">{children}</div>
      </div>

      <Footer />
    </div>
  );
}

export function PrivacyPolicy() {
  return (
    <LegalLayout
      title="Privacy Policy"
      subtitle={`Last updated: ${EFFECTIVE_DATE}`}
    >
      <p>
        classBoon ("classBoon," "we," "us") provides classroom tools for student
        sign-outs, attendance, emergency accountability, and related school
        operations. This policy explains what information we collect, how we use
        it, and the rights of schools, teachers, students, and families. We built
        classBoon for use by schools and educators, and we treat student data as
        belonging to the school.
      </p>

      <h2>1. Who controls the data</h2>
      <p>
        When a school or its staff uses classBoon, the <strong>school is the data
        controller</strong> and classBoon acts as a service provider ("school
        official" under FERPA) processing data on the school's behalf and under
        its direction. The school decides what student information is entered and
        for how long it is kept.
      </p>

      <h2>2. Information we collect</h2>
      <p>
        <strong>Account information (teachers / staff / admins):</strong> name,
        email address, password (stored hashed by our authentication provider) or
        Google sign-in identifier, school affiliation, and role.
      </p>
      <p>
        <strong>Student information (entered by school staff):</strong> first and
        last name, a school-assigned student ID, grade level, class/homeroom
        enrollment, and — depending on the features a school uses — attendance
        records, sign-out and return events (time, destination, and duration),
        emergency check-in status, lending records, and lunch selections.
      </p>
      <p>
        <strong>Usage and technical data:</strong> basic device/browser
        information and aggregate analytics to keep the service running and
        improve it. We do not build advertising profiles and we do not sell data.
      </p>
      <p>
        classBoon is not directed to children for independent sign-up. Where a
        feature allows students to sign in, accounts are provisioned and
        authorized by the school, which provides consent on behalf of parents as
        permitted under FERPA and COPPA's school-consent provisions.
      </p>

      <h2>3. How we use information</h2>
      <ul>
        <li>To provide the features the school has chosen (attendance, sign-out, emergency accountability, rosters, lending, insights).</li>
        <li>To authenticate users and keep accounts and data secure.</li>
        <li>To support, maintain, troubleshoot, and improve the service.</li>
        <li>To comply with law and respond to lawful requests from the school.</li>
      </ul>
      <p>
        We do <strong>not</strong> use student data for advertising, sell it, or
        share it with third parties for their own purposes.
      </p>

      <h2>4. How information is stored and shared</h2>
      <p>
        classBoon runs on Google Firebase (Google Cloud Platform), which hosts the
        application and stores its data. Google acts as our sub-processor under its
        own security and privacy commitments. Access to student data within
        classBoon is restricted to authenticated users at the same school, scoped
        by role, and enforced by server-side security rules. We may share
        information only: (a) with the school that controls it; (b) with service
        providers like Google that operate the platform under confidentiality
        obligations; or (c) when required by law.
      </p>

      <h2>5. Data security</h2>
      <p>
        Data is encrypted in transit (HTTPS) and at rest by our cloud provider.
        Access is governed by per-school, role-based security rules. No system is
        perfectly secure, but we work to protect data using industry-standard
        safeguards and will notify the affected school without undue delay if we
        become aware of a breach affecting its data.
      </p>

      <h2>6. Data retention and deletion</h2>
      <p>
        Schools control retention. A school administrator can remove students,
        archive classes, and graduate or detach records. On a school's verified
        request, classBoon will delete or return the school's data within a
        reasonable period. Records kept for historical accountability (for
        example, a completed emergency drill) are retained until the school
        directs otherwise. To request export or deletion, contact us at{" "}
        <a href={`mailto:${SUPPORT_EMAIL}`}>{SUPPORT_EMAIL}</a>.
      </p>

      <h2>7. Family rights (FERPA)</h2>
      <p>
        Because the school controls the records, parents and eligible students
        should direct requests to access, review, or correct student information
        to their school. The school may use classBoon's tools, or ask us, to
        fulfill those requests.
      </p>

      <h2>8. Changes to this policy</h2>
      <p>
        We may update this policy as the service evolves. Material changes will be
        reflected by a new "Last updated" date, and where appropriate we will
        notify schools.
      </p>

      <h2>9. Contact</h2>
      <p>
        Questions about this policy or a data request? Email{" "}
        <a href={`mailto:${SUPPORT_EMAIL}`}>{SUPPORT_EMAIL}</a>.
      </p>
    </LegalLayout>
  );
}

export function TermsOfService() {
  return (
    <LegalLayout
      title="Terms of Service"
      subtitle={`Last updated: ${EFFECTIVE_DATE}`}
    >
      <p>
        These Terms govern your use of classBoon. By creating an account or using
        the service, you agree to them. classBoon is intended for use by schools,
        educators, and staff acting on behalf of a school.
      </p>

      <h2>1. Accounts and eligibility</h2>
      <p>
        You must be an educator, staff member, or administrator authorized by your
        school to create an account. You are responsible for the accuracy of the
        information you enter, for activity under your account, and for keeping
        your credentials secure. Student records must only be entered by staff
        authorized by the school to do so.
      </p>

      <h2>2. Acceptable use</h2>
      <ul>
        <li>Use classBoon only for legitimate school and classroom purposes.</li>
        <li>Do not enter information you are not authorized to collect or share.</li>
        <li>Do not attempt to access data belonging to another school or user.</li>
        <li>Do not misuse, disrupt, reverse-engineer, or overload the service.</li>
      </ul>

      <h2>3. Student data ownership</h2>
      <p>
        Student data entered into classBoon belongs to the school. classBoon
        claims no ownership of it and processes it solely to provide the service,
        as described in our{" "}
        <a href="/privacy">Privacy Policy</a>. The school may export or request
        deletion of its data.
      </p>

      <h2>4. Availability</h2>
      <p>
        We work to keep classBoon reliable but provide it on an "as available"
        basis and may modify, suspend, or discontinue features. classBoon is a
        convenience tool and should not be your sole system of record for legally
        required attendance or for life-safety decisions during a real emergency;
        always follow your school's official procedures.
      </p>

      <h2>5. Disclaimers and limitation of liability</h2>
      <p>
        The service is provided "as is" without warranties of any kind, to the
        fullest extent permitted by law. classBoon is not liable for indirect,
        incidental, or consequential damages arising from use of the service. This
        does not limit any rights that cannot be limited under applicable law.
      </p>

      <h2>6. Termination</h2>
      <p>
        You may stop using classBoon at any time. We may suspend or terminate
        access for violations of these Terms. On termination, the data-handling
        commitments in our Privacy Policy continue to apply to your school's data.
      </p>

      <h2>7. Governing law</h2>
      <p>
        These Terms are governed by the laws of the State of New Jersey, without
        regard to conflict-of-laws principles, unless a separate written agreement
        with your school or district says otherwise.
      </p>

      <h2>8. Contact</h2>
      <p>
        Questions about these Terms? Email{" "}
        <a href={`mailto:${SUPPORT_EMAIL}`}>{SUPPORT_EMAIL}</a>.
      </p>
    </LegalLayout>
  );
}

export function Support() {
  const navigate = useNavigate();
  return (
    <LegalLayout
      title="Support"
      subtitle="We're here to help — and we read every message."
    >
      <p>
        classBoon is built and supported by a teacher. If something isn't working,
        you have a question, or you'd like to request a feature, please reach out.
      </p>

      <h2>Email us</h2>
      <p>
        <a href={`mailto:${SUPPORT_EMAIL}`}>{SUPPORT_EMAIL}</a>
      </p>
      <p>To help us resolve issues quickly, please include:</p>
      <ul>
        <li>Your name, school, and role.</li>
        <li>What you were trying to do and what happened instead.</li>
        <li>The page or feature involved (for example, Attendance or Emergency).</li>
        <li>A screenshot, if you have one.</li>
      </ul>

      <h2>For school administrators &amp; IT</h2>
      <p>
        Reviewing classBoon for your school or district? We can provide our
        privacy practices, a data privacy agreement, and the list of domains your
        network should allow. See our{" "}
        <a href="/privacy">Privacy Policy</a> or email{" "}
        <a href={`mailto:${SUPPORT_EMAIL}`}>{SUPPORT_EMAIL}</a>.
      </p>

      <div className="legal-actions">
        <button
          className="landing-btn"
          onClick={() => navigate("/about")}
        >
          How classBoon works
        </button>
      </div>
    </LegalLayout>
  );
}
